import { genericMemo } from "@redotech/react-util/component";
import {
  OmsDeliveryMethodConditionStep,
  OmsMarketplaceConditionStep,
} from "@redotech/redo-flows-common/oms/conditions";
import { OmsConditionStep } from "@redotech/redo-flows-common/steps";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { useMemo, useRef, useState } from "react";
import { DeepPartial } from "..";

export const ListMatchFixedValuesInput = genericMemo(
  function ListMatchFixedValuesInput<T extends string>({
    step,
    setStep,
    label,
    options,
  }: {
    step:
      | DeepPartial<OmsDeliveryMethodConditionStep>
      | DeepPartial<OmsMarketplaceConditionStep>;
    setStep: (step: DeepPartial<OmsConditionStep>) => void;
    label: string;
    options: { id: string; label: string; value: T }[];
  }) {
    const items = useMemo(
      () =>
        options.map(({ id, label, value }) => ({
          type: "text" as const,
          text: label,
          value,
          id,
        })),
      [options],
    );
    const selectedItems = useMemo(
      () =>
        items.filter(({ value }) =>
          (step.matchValues as (string | undefined)[] | undefined)?.includes(
            value,
          ),
        ),
      [items, step.matchValues],
    );

    const anchorRef = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);

    return (
      <>
        <Flex>
          <RedoButton
            hierarchy="secondary"
            IconTrailing={ChevronDownIcon}
            onClick={() => setOpen(!open)}
            ref={anchorRef}
            text={
              selectedItems.length > 0
                ? selectedItems.map(({ text }) => text).join(", ")
                : label
            }
          />
        </Flex>
        <RedoMultiselectDropdown<T>
          dropdownAnchor={anchorRef.current}
          dropdownOpen={open}
          items={items}
          selectedItems={selectedItems}
          setDropdownOpen={setOpen}
          setSelectedItems={(selectedItems) => {
            setStep({
              ...step,
              matchValues: selectedItems.map(({ value }) => value),
            } as DeepPartial<OmsConditionStep>);
          }}
        />
      </>
    );
  },
);
